import { Box, Stack, Container, Typography, IconButton, Tooltip } from "@material-ui/core";
import SettingsIcon from "@material-ui/icons/Settings";
import moment from "moment";
import "moment/locale/he"; // without this line, language is not imported
import Page from "../../../components/Page";
import CallsListGrid from "components/__haim/CallsList/CallsListGrid";

// hooks
import useSettings from "../../../hooks/useSettings";
import { virtualNumberCallsColumns } from "logic/CrmSpecifications/GenericCrmSpecifications";
import { useCallsData } from "./hooks/useCallsData";
import { PeriodsAgo } from "../../../@types/custom";
import { useState, useMemo, useEffect } from "react";
import { MembersList } from "./MembersList";
import { PeriodsSelect } from "./PeriodsSelect";
import { useTranslation } from "react-i18next";
import { LoadingOverlay } from "components/__haim/Common/LoadingOverlay";
import { initialDateRangeFilter } from "utils/listings/table";
import { CallFilterDate } from "./CallFilterDate";
import type { ChangeEvent } from "react";
import type { DateRangeFilter } from "utils/listings/table";
import { AgGridColumnProps } from "ag-grid-react";
import { db } from "firebaseConfig";
import useAuth from "hooks/useAuth";
import { useModal } from "mui-modal-provider";
import { GenericConverter } from "utils/firestoreConverters";
import { CallTranscriptAndSummary } from "./../../../@types/sharedSchema";
import { LoadingButton } from "@material-ui/lab";
import TranscriptionDialog from "./TranscriptionDialog";
import { SettingsPanel } from "components/__haim/Settings/SettingsPanel";
import useVirtualNumbers from "hooks/useVirtualNumbers";

type CallsListPageProps = {
  fromPhoneNumber?: string;
};

const DEFAULT_SELECTED_PERIOD = PeriodsAgo.month;

const DEFAULT_CUSTOM_DATE_FILTER: DateRangeFilter = {
  ...initialDateRangeFilter,
  min: getDefaultStartDate()
};

export default function CallsListPage({ fromPhoneNumber }: CallsListPageProps): JSX.Element {
  const [selectedPeriod, setSelectedPeriod] = useState(DEFAULT_SELECTED_PERIOD);
  const { user } = useAuth();
  const { showModal } = useModal();

  const [previousSelectedPeriod, setPreviousSelectedPeriod] = useState<
    PeriodsAgo | DateRangeFilter
  >(DEFAULT_SELECTED_PERIOD);

  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);

  const [customDateFilter, setCustomDateFilter] = useState<DateRangeFilter>(
    DEFAULT_CUSTOM_DATE_FILTER
  );

  const { themeStretch } = useSettings();
  const { t } = useTranslation();

  // Tags are the customer's own labels for what each virtual number is used for
  // ("Yad2 ad", "billboard", ...). The dropdown shows them so a number can be
  // recognised by its source rather than by its digits.
  const { tagsByNumber } = useVirtualNumbers();

  // Local state, NOT `showModal`. `ModalProvider` is mounted above `FirestoreProvider`
  // in App.tsx, so anything it mounts is outside the contexts settings sections read —
  // see the warning in `SettingsPanel`.
  const [isVirtualNumbersSettingsOpen, setIsVirtualNumbersSettingsOpen] = useState(false);

  const openVirtualNumbersSettings = (): void => setIsVirtualNumbersSettingsOpen(true);

  const isUsingCustomDateFilter = selectedPeriod === PeriodsAgo.custom;

  useEffect(() => {
    if (!isUsingCustomDateFilter) return;

    const bothCustomDateFilterFilled = customDateFilter.min && customDateFilter.max;
    const validDateRangeFilterMinMax =
      customDateFilter.min < customDateFilter.max && customDateFilter.max > customDateFilter.min;

    const validDateRangeFilter = bothCustomDateFilterFilled && validDateRangeFilterMinMax;

    if (!validDateRangeFilter) return;

    setPreviousSelectedPeriod(customDateFilter);
  }, [isUsingCustomDateFilter, customDateFilter]);

  const parsedSelectedPeriod = isUsingCustomDateFilter ? previousSelectedPeriod : selectedPeriod;

  const { calls, members, myTeamData, isLoadingCallsData } = useCallsData(
    selectedMembers,
    parsedSelectedPeriod,
    fromPhoneNumber
  );

  const handleMemberClick = (value: string[]): void => {
    // toggle button
    if (value.includes("toggle")) {
      // all selected - now deselect all
      if (selectedMembers.length === members.length) {
        setSelectedMembers([]);
      } else {
        // select all
        setSelectedMembers(members);
      }
      return;
    }

    setSelectedMembers(value);
  };

  const handlePeriodChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value as PeriodsAgo;

    setSelectedPeriod(value);

    if (value !== PeriodsAgo.custom) {
      setCustomDateFilter(DEFAULT_CUSTOM_DATE_FILTER);
      setPreviousSelectedPeriod(value);
    }
  };

  const recordingOpenTranscriptionCol: AgGridColumnProps = useMemo(
    () => ({
      headerName: "תמלול שיחה",
      field: "transcriptionData",
      valueGetter: (params) => {
        if (params.data.transcriptionData !== undefined) {
          return params.data.transcriptionData;
        }

        console.log("Got row data:", params.node.rowIndex, params.data);
        const transcriptionDocPath = `${params.data._metadata.firestorePath}/enrichment/transcriptAndSummary`;
        db.doc(transcriptionDocPath)
          .withConverter(GenericConverter<CallTranscriptAndSummary>())
          .get()
          .then((doc) => {
            console.log("Trascription exists?", doc.exists);
            params.data.transcriptionData = doc.data() ?? null;
            params.api.refreshCells({ columns: [params.column], rowNodes: [params.node] });
          });
      },
      cellRendererFramework: (params) => {
        return params.data.transcriptionData === undefined ? (
          `🔄 ...`
        ) : params.data.transcriptionData === null ? (
          `-`
        ) : (
          <LoadingButton
            color="info"
            loading={params.data.transcriptionData === undefined}
            onClick={() => {
              showModal(TranscriptionDialog, {
                callTranscriptAndSummary: params.data.transcriptionData
              });
            }}
            size={"small"}
            variant="text"
            loadingPosition="end"
            // endIcon={<Icon icon={addIcon} />}
            fullWidth
          >
            {t("callsPage.openTranscription")}
          </LoadingButton>
        );
      }
      //   return params.data?.isContact === true ? (
      //     `✅ ${translate("leads.savedContact")}`
      //   ) : (
      //     <LoadingButton
      //       color="info"
      //       loading={params.data?.isContact === undefined}
      //       onClick={() => {
      //         let contactForEdit = {
      //           id: params.data.phoneNumber,
      //           contactOrigin: "Webot virtual number",
      //           contactOriginAdditionalInfo: params.data.virtualNumber,
      //           _metadata: {
      //             parsedPhoneNumber: params.data._metadata.phoneNumberObj
      //           }
      //         };
      //         let columnsForNewContact = currentEditableDataColumns;

      //         showModal(ContactEditDialog, {
      //           contact: contactForEdit,
      //           columns: columnsForNewContact,
      //           updateContact: async (
      //             contactId: string,
      //             data: Object,
      //             enforceCreateTime?: boolean
      //           ): Promise<void> => {
      //             await updateContact(contactId, data, enforceCreateTime);
      //             params.data.isContact = undefined;
      //             params.api.refreshCells({ columns: [params.column], rowNodes: [params.node] });
      //           }
      //         });
      //       }}
      //       size={"small"}
      //       variant="contained"
      //       loadingPosition="end"
      //       endIcon={<Icon icon={addIcon} />}
      //       fullWidth
      //     >
      //       {translate("leads.createNewContact")}
      //     </LoadingButton>
      //   );
      // }
    }),
    [showModal]
  );

  const tableColumns = useMemo(
    () => [
      ...virtualNumberCallsColumns,
      recordingOpenTranscriptionCol,
      {
        field: "owner",
        headerName: t("general.owner"),
        cellStyle: { "font-weight": "bold" },
        cellRenderer: "VirtualNumberOwnerRenderer",
        cellRendererParams: { tagsByNumber },
        minWidth: 250
      }
    ],
    [t, recordingOpenTranscriptionCol, tagsByNumber]
  );

  return (
    <Page title="Webot - Calls">
      <Container maxWidth={themeStretch ? false : "lg"}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ height: 60 }}
        >
          <Typography variant="h4">{t("callsPage.summary")}</Typography>
          {/* Opens the virtual-number settings (tags, no-answer bot) — customers
              asked to manage their numbers straight from the calls page. */}
          <Tooltip title={t("virtualNumbers.openSettings") as string}>
            <IconButton
              onClick={openVirtualNumbersSettings}
              aria-label={t("virtualNumbers.openSettings")}
            >
              <SettingsIcon />
            </IconButton>
          </Tooltip>
        </Stack>
        <Stack gap={2}>
          <Stack flexWrap="wrap" direction="row" gap={1.5}>
            <PeriodsSelect value={selectedPeriod} onChange={handlePeriodChange} />
            {isUsingCustomDateFilter && (
              <Stack sx={{ width: { xs: "100%", md: 420 } }}>
                <CallFilterDate value={customDateFilter} onChange={setCustomDateFilter} />
              </Stack>
            )}
            <MembersList
              members={members}
              myTeamData={myTeamData}
              selectedMembers={selectedMembers}
              handleMemberClick={handleMemberClick}
              tagsByNumber={tagsByNumber}
            />
          </Stack>
          <Box position="relative">
            <LoadingOverlay isLoading={isLoadingCallsData} />
            <CallsListGrid tableColumns={tableColumns} tableRows={calls} />
          </Box>
        </Stack>

        <SettingsPanel
          sectionId="virtualNumbers"
          open={isVirtualNumbersSettingsOpen}
          onClose={() => setIsVirtualNumbersSettingsOpen(false)}
        />
      </Container>
    </Page>
  );
}

export function getDefaultStartDate(): string {
  return moment().subtract(30, "days").format("YYYY-MM-DD");
}
